import { Injectable, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AlertService } from './alert.service';

/**
 * Shows every error in the app as the error dialog (red ✕ popup) instead of an error toast.
 *
 * Code all over the app calls `toastr.error(message, title)`. Rather than change each call,
 * `toastr.error` is redirected here once at start-up. Success / info / warning toasts are unchanged.
 *
 * If several errors arrive while a dialog is open (e.g. two API calls failing together),
 * only the first is shown, so the user isn't buried under stacked popups.
 */
@Injectable({ providedIn: 'root' })
export class ErrorDialogBridgeService {
  private readonly toastr = inject(ToastrService);
  private readonly alert = inject(AlertService);

  private started = false;
  private dialogOpen = false;

  start(): void {
    if (this.started) return;
    this.started = true;

    (this.toastr as any).error = (message?: any, title?: any) => {
      this.showError(message, title);
      return undefined;
    };
  }

  private showError(message?: any, title?: any): void {
    if (this.dialogOpen) return;

    const text = this.toText(message) || 'Something went wrong. Please try again.';
    // Toast titles are often just "Error"; use them as the heading when they say more
    const heading = this.toText(title) || 'Error';

    this.dialogOpen = true;
    Promise.resolve(this.alert.error({ heading, message: text }))
      .finally(() => (this.dialogOpen = false));
  }

  /** Error messages sometimes arrive as objects or arrays from the API. */
  private toText(value: any): string {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return value.map(v => this.toText(v)).filter(Boolean).join('\n');
    if (typeof value === 'object') return this.toText(value.message ?? value.error ?? '');
    return String(value);
  }
}
